import { WORKSPACE_SLUG, slugFrom, type WorkspaceSettings } from '@forecastify/shared';
import { Select, Switch, TextInput } from '@forecastify/ui';
import { COUNTRIES, countryName } from '../countries';
import { useT } from '../../i18n';
import { Field } from '../card';
import { SettingsCard } from './settings-shell';
import { SettingsPage } from './settings-page';
import { DropZone } from '../drop-zone';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ Általános — `markup.html` 1750–1830.                                       │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * Two cards and one footer: the workspace's identity, and the defaults an invitation copies.
 *
 * ⚠️⚠️ **THE MOCKUP DRAWS FOUR CONTROLS IN THE SECOND CARD AND THIS DRAWS TWO.** `date_format` and
 * `week_start` are not built, and `0022_workspace_settings.sql` carries the argument: every date in
 * this product renders as its ISO wire value in one shape, and the week is Monday-based in the single
 * helper C12's calendar and C15's period presets both depend on. Two settings that no renderer
 * consults would be the "visible control that does nothing" failure this project has shipped most
 * often — so they are recorded in `chunks.md` with what it would cost to make them real, rather than
 * rendered inert.
 *
 * ⚠️⚠️ **THE LOGO PICKER IS NOT BUILT EITHER, AND FOR A BIGGER REASON.** An uploaded image needs
 * object storage: a bucket, its policies, signed URLs, a MIME check and a size limit. That is a
 * subsystem this repo does not have yet and a decision rather than a form field — so the initial tile
 * renders, which is exactly what the mockup shows for its own `logo: null`.
 */

export interface GeneralDraft {
  name: string;
  slug: string;
  defaultLanguage: 'hu' | 'en';
  defaultTimezone: string;
  country: string;
  notificationEmails: boolean;
}

/** The mockup's own timezone list, the same one the onboarding wizard offers. */
const TIMEZONES = [
  'Europe/Budapest',
  'Europe/Berlin',
  'Europe/London',
  'Europe/Bucharest',
  'America/New_York',
  'UTC',
];

export function draftOf(settings: WorkspaceSettings): GeneralDraft {
  return {
    name: settings.name,
    slug: settings.slug,
    defaultLanguage: settings.defaultLanguage,
    defaultTimezone: settings.defaultTimezone,
    country: settings.country,
    notificationEmails: settings.notificationEmails,
  };
}

/**
 * What the slug note says, and in which colour.
 *
 * ⚠️ `reserved` is its own state rather than folded into `taken`, because 0098's endpoint tells
 * the two apart and *"that address is taken"* about `sign-in` sends a person looking for whoever
 * took it. Both block the Save; only the wording differs.
 */
export type SlugState = 'idle' | 'checking' | 'free' | 'taken' | 'reserved' | 'invalid';

export interface GeneralSectionProps {
  readonly settings: WorkspaceSettings;
  readonly draft: GeneralDraft;
  readonly slugState: SlugState;
  readonly canManage: boolean;
  readonly saving: boolean;
  readonly serverError: string | null;
  readonly onDraft: (patch: Partial<GeneralDraft>) => void;
  readonly onReset: () => void;
  readonly onSave: () => void;
  /**
   * ┌───────────────────────────────────────────────────────────────────────┐
   * │ ⚠️ ITEM 59 · the workspace's picture, uploaded from the ROUTE.        │
   * └───────────────────────────────────────────────────────────────────────┘
   *
   * ⚠️⚠️ **THE FIRST VERSION PUT `useSession` IN THIS FILE AND SIX TESTS SAID SO IMMEDIATELY** —
   * *"useSession was called outside SessionProvider"*. Every screen in this app is presentational
   * and its tests render it bare; the route fetches and knows about addresses. `projects-screen.tsx`
   * records the identical mistake with `useSearchParams` and the same one-line lesson.
   *
   * ⚠️ Optional, so an embedded copy and every existing test keep working without them.
   */
  readonly iconUrl?: string | undefined;
  readonly uploading?: boolean;
  readonly onIcon?: (file: File) => void;
  /** Already translated. ⚠️ Shown, because a silent upload is what was reported. */
  readonly iconError?: string | null;
}

export function GeneralSection(props: GeneralSectionProps) {
  const t = useT();
  const { settings, draft, slugState, canManage, iconUrl, uploading = false } = props;

  const dirty =
    draft.name !== settings.name ||
    draft.slug !== settings.slug ||
    draft.defaultLanguage !== settings.defaultLanguage ||
    draft.defaultTimezone !== settings.defaultTimezone ||
    draft.country !== settings.country ||
    draft.notificationEmails !== settings.notificationEmails;

  /**
   * ⚠️ **The Save is blocked while the slug is unusable**, not merely warned about. The mockup lets
   * it through and reports the collision afterwards; a form that can be submitted into a refusal it
   * already knows about is a form that wastes the round trip.
   */
  const slugBlocked =
    slugState === 'taken' ||
    slugState === 'reserved' ||
    slugState === 'invalid' ||
    slugState === 'checking';

  /**
   * ┌───────────────────────────────────────────────────────────────────────┐
   * │ ⚠️⚠️ AN EXPLICIT SAVE AGAIN — 13 Oct, the product owner's call.        │
   * └───────────────────────────────────────────────────────────────────────┘
   *
   * The 15 Sep auto-save (`useAutoSave`) is gone from this page: the draft is held by the route
   * and written only when somebody presses *Mentés*. It sits on the title row, where every
   * settings page puts its primary action, with *Elvetés* beside it. Both are disabled when
   * there is nothing to save or discard, and Save also while the slug is refused or a write is
   * in flight — a button that is always live makes "did that do anything?" a question.
   *
   * ⚠️ **The workspace icon is still immediate.** It is a file upload with its own busy state
   * and its own refusals, not a field of this draft — holding a picked file until Save would
   * mean a preview of bytes the server has not scanned yet.
   */
  const saveAction = !canManage
    ? undefined
    : {
        label: props.saving ? t('common.saving') : t('common.save'),
        icon: 'check' as const,
        disabled: !dirty || slugBlocked || props.saving,
        onSelect: props.onSave,
      };
  const discardAction = !canManage
    ? undefined
    : {
        label: t('common.discard'),
        disabled: !dirty || props.saving,
        onSelect: props.onReset,
      };

  return (
    /* ⚠️ C67 · The one form screen in the batch. It keeps the 680px column — a field stretched
       across a wide monitor is a worse form, not a wider one — and gains the title row every
       other settings page now carries. */
    <div data-screen="settings-general" className="flex min-h-0 flex-1 flex-col">
      <SettingsPage
        title={t('settings.nav.general')}
        width="form"
        action={saveAction}
        secondaryAction={discardAction}
      >
        <div className="flex flex-col gap-4">
          <SettingsCard title={t('settings.general.identity')} tourId="settings-identity">
            {/**
             * ┌───────────────────────────────────────────────────────────────────┐
             * │ ⚠️⚠️ AN UPLOAD, WHERE THE NOTE HERE SAID THERE COULD NOT BE ONE.  │
             * └───────────────────────────────────────────────────────────────────┘
             *
             * It read: *"the picker beside it needs object storage, which is a subsystem rather than a
             * field"*. That was true when it was written and stopped being true at C42 — `0061` added
             * `file_target.workspace_icon`, `file.service.ts` serves
             * `/files/workspace-icon/:id`, and the create-workspace dialog has uploaded one ever
             * since. Only this screen never caught up, so an icon could be set when a workspace was
             * MADE and never afterwards.
             *
             * ⚠️ **`forgetAvatars()` after the upload — item 59's *"updates the switcher"*.** The
             * shell fetches every workspace's icon through `useAvatars`, which caches by path; the
             * path does not change when the bytes do, so without this the sidebar keeps showing the
             * old picture until a reload. `use-avatar.ts` records why a module-level counter nothing
             * subscribes to changes nothing on screen.
             */}
            <div className="mt-3 flex items-center gap-4">
              <span
                aria-hidden
                className="bg-primary text-primary-foreground grid size-14 shrink-0 place-items-center overflow-hidden rounded-xl text-[18px] font-semibold"
              >
                {iconUrl === undefined ? (
                  settings.name.slice(0, 1).toUpperCase()
                ) : (
                  <img src={iconUrl} alt="" className="size-full object-cover" />
                )}
              </span>

              <DropZone
                accept={['image/png', 'image/jpeg', 'image/webp', 'image/svg+xml']}
                disabled={!canManage}
                busy={uploading}
                className="min-w-0 flex-1"
                onFiles={(files) => {
                  const first = files[0];
                  if (first !== undefined) props.onIcon?.(first);
                }}
              />
            </div>

            {/* ⚠️ Item 12's second half: a refusal has to be visible. `settings-route.tsx` names the
            four codes the server can answer with, in the avatar upload's own words. */}
            {props.iconError == null ? null : (
              <p role="alert" className="text-destructive mt-2 text-[11.5px]">
                {props.iconError}
              </p>
            )}

            <div className="mt-3 flex flex-col gap-3">
              <Field label={t('settings.general.name')}>
                <TextInput
                  value={draft.name}
                  disabled={!canManage}
                  onChange={(event) => {
                    const name = event.target.value;
                    /* 26 Oct 2026 · the address follows the name until somebody edits it. */
                    props.onDraft(
                      draft.slug === addressFor(settings, draft.name)
                        ? { name, slug: addressFor(settings, name) }
                        : { name },
                    );
                  }}
                />
              </Field>

              {/*
            ⚠️⚠️ **THE ADDRESS FIELD IS BACK — 26 Oct 2026, reversing item 60.** Item 60 took it
            away on the grounds that the slug appeared in no URL — true when the round was written,
            and untrue since R-38 put it first in every address. A rename then left the address on
            the old name with no way to move it.

            ⚠️ **It follows the name** (`addressFor`) until somebody types into it, so renaming
            moves the address by default and the new one is on screen, checked, before Save. Old
            links are NOT redirected — the product owner's call; `debt.md` keeps the history table
            as the way to do it. ⚠️ No domain beside it and no monospace: nothing serves a subdomain,
            and Inter sets everything (CLAUDE.md §3).
          */}
              <Field
                label={t('settings.general.slug')}
                {...(slugRefused(slugState)
                  ? { error: slugNote(t, slugState, draft.slug) }
                  : { note: slugNote(t, slugState, draft.slug) })}
              >
                <TextInput
                  value={draft.slug}
                  disabled={!canManage}
                  spellCheck={false}
                  autoCapitalize="none"
                  onChange={(event) => {
                    props.onDraft({ slug: event.target.value });
                  }}
                />
              </Field>
            </div>
          </SettingsCard>

          <SettingsCard title={t('settings.general.defaults')} tourId="settings-defaults">
            <div className="mt-3 flex flex-col gap-3">
              <Field label={t('settings.general.language')}>
                <Select
                  value={draft.defaultLanguage}
                  disabled={!canManage}
                  onChange={(event) => {
                    props.onDraft({ defaultLanguage: event.target.value === 'en' ? 'en' : 'hu' });
                  }}
                  options={[
                    { value: 'hu', label: t('people.language.hu') },
                    { value: 'en', label: t('people.language.en') },
                  ]}
                />
              </Field>

              <Field label={t('settings.general.timezone')}>
                <Select
                  value={draft.defaultTimezone}
                  disabled={!canManage}
                  onChange={(event) => {
                    props.onDraft({ defaultTimezone: event.target.value });
                  }}
                  options={[
                    ...TIMEZONES.map((zone) => ({ value: zone, label: zone })),
                    /* ⚠️ The stored zone stays selectable even when it is not in the list — a picker that
                     * cannot represent its own value silently changes it on the next save. */
                    ...(TIMEZONES.includes(draft.defaultTimezone)
                      ? []
                      : [{ value: draft.defaultTimezone, label: draft.defaultTimezone }]),
                  ]}
                />
              </Field>

              {/**
               * ⚠️⚠️ **THE COUNTRY IS NOT A DEFAULT AN INVITATION COPIES, WHICH IS WHY THE NOTE UNDER IT
               * SAYS WHAT IT IS FOR.** It sits in this card because there is nowhere better and the
               * card is about where the workspace sits in the world — but the two above it seed a new
               * member and this one is read by the public-holiday import. `decisions.md` P3-4.
               */}
              <Field label={t('settings.general.country')} tourId="settings-country">
                <Select
                  value={draft.country}
                  disabled={!canManage}
                  onChange={(event) => {
                    props.onDraft({ country: event.target.value });
                  }}
                  options={[
                    ...COUNTRIES.map((code) => ({ value: code, label: countryName(t, code) })),
                    /* ⚠️ The stored country stays selectable even when it is not in the list, for the
                   timezone picker's reason: a control that cannot represent its own value changes
                   it on the next save. */
                    ...(COUNTRIES.includes(draft.country as (typeof COUNTRIES)[number])
                      ? []
                      : [{ value: draft.country, label: countryName(t, draft.country) }]),
                  ]}
                />
              </Field>

              {/* ⚠️ Said out loud: these apply to somebody who has not joined yet. The mockup's own card
              copy says the same, and without it a viewer would wonder why changing the language did
              nothing to the screen they are looking at. */}
            </div>
          </SettingsCard>

          {/**
           * ⚠️ `0195` · An addition: the mockup draws no workspace-wide switch for email, so the row
           * is the account notifications screen's own pause row — a label and a `Switch`. Email
           * only; the lede says the bell keeps working, because "notifications off" would otherwise
           * read as the inbox going quiet too.
           */}
          <SettingsCard
            title={t('settings.general.notifications')}
            lede={t('settings.general.notificationEmailsLede')}
          >
            <div className="mt-3 flex items-center gap-3">
              <span className="flex-1 text-[12.5px]">
                {t('settings.general.notificationEmails')}
              </span>
              <Switch
                label={t('settings.general.notificationEmails')}
                checked={draft.notificationEmails}
                disabled={!canManage}
                onCheckedChange={(next) => {
                  props.onDraft({ notificationEmails: next });
                }}
              />
            </div>
          </SettingsCard>

          {props.serverError !== null && (
            <p
              role="alert"
              className="border-destructive/40 bg-destructive/10 text-destructive rounded-md border px-3 py-2 text-[12.5px]"
            >
              {props.serverError}
            </p>
          )}
        </div>
      </SettingsPage>
    </div>
  );
}

/**
 * The address a name would move the workspace to.
 *
 * ⚠️ **The stored slug while the name is the stored name**, so typing a name and then putting it
 * back leaves the address where it was — `delivery-3` is not quietly turned into `delivery`. A name
 * too short to make a slug (`slugFrom` answers `''`) keeps the stored one too.
 *
 * ⚠️ **"Following" is not a flag, it is this function agreeing with the draft.** The name field
 * moves the address only while `draft.slug === addressFor(settings, draft.name)`; typing anything
 * else into the address breaks the match and the name stops moving it. Discarding or saving
 * re-seeds the draft from the stored row, which agrees again.
 */
export function addressFor(settings: WorkspaceSettings, name: string): string {
  if (name === settings.name) return settings.slug;
  const slug = slugFrom(name);
  return slug === '' ? settings.slug : slug;
}

/** The three answers that refuse the Save, drawn as the field's error rather than its note. */
function slugRefused(state: SlugState): boolean {
  return state === 'taken' || state === 'reserved' || state === 'invalid';
}

/**
 * What the address says under itself. ⚠️ `reserved` apart from `taken` — `0098`: *"that address is
 * taken"* about `sign-in` sends somebody looking for whoever took it.
 */
function slugNote(t: ReturnType<typeof useT>, state: SlugState, slug: string): string {
  if (state === 'checking') return t('settings.slug.checking');
  if (state === 'taken') return t('settings.slug.taken');
  if (state === 'reserved') return t('settings.slug.reserved');
  if (state === 'invalid') return t('settings.slug.invalid');
  if (state === 'free') return t('settings.slug.free', { slug });
  return t('settings.slug.idle');
}

/** Whether a typed slug is even worth asking the server about. */
export function slugLooksValid(slug: string): boolean {
  return WORKSPACE_SLUG.test(slug);
}
